import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

export interface FlashcardCategory {
  id?: string;
  name: string;
  description: string;
  created_at?: string;
}

export interface Flashcard {
  id?: string;
  type?: 'image' | 'text' | 'cloze';
  text: string;
  description: string;
  topic?: string;
  youtube_link?: string;
  is_active: number | string;
  flashcard_category_id: number;
  image_path?: string;
  question_image_path?: string;
  images?: { id: string; image_path: string }[];
  question_images?: { id: string; image_path: string }[];
  created_at?: string;
  category_name?: string;
  urutan?: number | string;
  order_number?: number | string;
  answer_id?: string;
  answer?: string;
  answer_status?: string;
  answer_created_at?: string;
  next_review?: string;
  review_count?: number;
  last_review?: string;
  category_card_count?: number;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  status: number;
}

@Injectable({
  providedIn: 'root'
})
export class FlashcardService {
  private apiUrl = 'https://oscemediko.id/api-timer/api/master/flashcard/categories';
  private flashcardApiUrl = 'https://oscemediko.id/api-timer/api/master/flashcard';

  constructor(private http: HttpClient) { }

  getCategories(): Observable<FlashcardCategory[]> {
    return this.http.get<ApiResponse<FlashcardCategory[]>>(`${this.apiUrl}/get`).pipe(
      map(response => response.data)
    );
  }

  addCategory(category: FlashcardCategory): Observable<FlashcardCategory> {
    return this.http.post<ApiResponse<FlashcardCategory>>(`${this.apiUrl}/add`, category).pipe(
      map(response => response.data)
    );
  }

  updateCategory(category: FlashcardCategory): Observable<any> {
    return this.http.post(`${this.apiUrl}/update`, category);
  }

  deleteCategory(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/delete`, { id });
  }

  // Flashcard methods
  getFlashcards(userId?: string): Observable<Flashcard[]> {
    const url = userId ? `${this.flashcardApiUrl}/get?user_id=${userId}&mode=all&limit=all` : `${this.flashcardApiUrl}/get?mode=all&limit=all`;
    return this.http.get<ApiResponse<Flashcard[]>>(url).pipe(
      map(response => response.data)
    );
  }

  getFlashcardsPaginated(page: number = 1, limit: number | string = 10, search?: string, categoryId?: string | number): Observable<ApiResponse<Flashcard[]>> {
    let url = `${this.flashcardApiUrl}/pagination/get?limit=${limit}&page=${page}&mode=all`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (categoryId) {
      url += `&category_id=${categoryId}`;
    }
    return this.http.get<ApiResponse<Flashcard[]>>(url);
  }

  getFlashcardsByCategory(categoryId: number, userId?: string, mode?: string, limit?: string | number): Observable<Flashcard[]> {
    let url = `${this.flashcardApiUrl}/get?category_id=${categoryId}`;
    if (userId) {
      url += `&user_id=${userId}`;
    }
    if (mode) {
      url += `&mode=${mode}`;
    }
    if (limit) {
      url += `&limit=${limit}`;
    }
    return this.http.get<ApiResponse<Flashcard[]>>(url).pipe(
      map(response => response.data)
    );
  }

  addFlashcard(flashcard: FormData): Observable<Flashcard> {
    return this.http.post<ApiResponse<Flashcard>>(`${this.flashcardApiUrl}/add`, flashcard).pipe(
      map(response => response.data)
    );
  }

  updateFlashcard(flashcard: FormData): Observable<any> {
    return this.http.post(`${this.flashcardApiUrl}/update`, flashcard);
  }

  deleteFlashcard(id: string): Observable<any> {
    return this.http.post(`${this.flashcardApiUrl}/delete`, { id });
  }

  deleteFlashcardImage(id: string): Observable<any> {
    return this.http.post(`${this.flashcardApiUrl}/delete-image`, { image_id: id, id });
  }

  saveAnswer(flashcardId: number, userId: string, answer: string, status: string): Observable<any> {
    return this.http.post(`${this.flashcardApiUrl}/answer/save`, {
      flashcard_id: flashcardId,
      user_id: userId,
      answer: answer,
      status: status
    });
  }

  getFlashcardsCustom(categoryIds: string, ratings: string, userId?: string): Observable<Flashcard[]> {
    let url = `${this.flashcardApiUrl}/get?category_id=${categoryIds}&ratings=${ratings}&mode=all&limit=all`;
    if (userId) {
      url += `&user_id=${userId}`;
    }
    return this.http.get<ApiResponse<Flashcard[]>>(url).pipe(
      map(response => response.data)
    );
  }
}